import {
  AfterViewInit,
  ComponentRef,
  Directive,
  ElementRef,
  Input,
  OnDestroy,
  Optional,
  Renderer2,
  Self,
  ViewContainerRef,
} from '@angular/core';
import { Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { NgControl } from '@angular/forms';
import { Subscription } from 'rxjs';
import { GtwSearchableSelectPanelComponent } from './gtw-searchable-select-panel.component';
import { GtwSearchableSelectOption } from './gtw-searchable-select.types';

/**
 * Non-invasive search-enabled wrapper around a native <select>. Apply as an
 * attribute on any existing <select>:
 *
 *     <select gtwSearchableSelect class="form-control input-sm"
 *             [(ngModel)]="value">
 *       <option [ngValue]="null">Pick one...</option>
 *       <option *ngFor="let x of list" [ngValue]="x">{{x.label}}</option>
 *     </select>
 *
 * Behaviour:
 *   - The native <select> stays in the DOM (display:none) so
 *     SelectControlValueAccessor, [(ngModel)], [ngValue], [compareWith] and
 *     (change) all keep working exactly as before.
 *   - A trigger DIV is injected as a sibling and inherits the same CSS classes
 *     as the host <select> so visual styling is preserved.
 *   - On click the trigger opens a CDK Overlay panel with a search input and
 *     a filterable list. Selecting an option programmatically sets
 *     nativeSelect.value and dispatches a bubbling 'change' event - Angular
 *     forms receives it identically to a native user click.
 *
 * Form integration:
 *   - When NgControl is present (template-driven or reactive forms), we listen
 *     to valueChanges to keep the trigger label in sync with external writes.
 *   - When no NgControl exists, we still subscribe to the native 'change' event.
 *
 * Limitations (intentional v1 scope):
 *   - No <optgroup> support.
 *   - Search is purely client-side over options currently rendered in the DOM.
 *   - Multi-select (<select multiple>) is not supported.
 */
@Directive({
  selector: 'select[gtwSearchableSelect]',
  standalone: false,
})
export class GtwSearchableSelectDirective implements AfterViewInit, OnDestroy {
  @Input() searchPlaceholder = 'Search...';
  @Input() emptyMessage = 'No results';
  /**
   * Text shown in the trigger when no option is selected (or selected option
   * has empty label). Defaults to the first option's label, then to a generic
   * placeholder.
   */
  @Input() triggerPlaceholder = 'Select...';

  private triggerEl: HTMLElement | null = null;
  private labelEl: HTMLElement | null = null;
  private caretEl: HTMLElement | null = null;
  private overlayRef: OverlayRef | null = null;
  private panelRef: ComponentRef<GtwSearchableSelectPanelComponent> | null = null;
  private panelSubs: Subscription | null = null;

  private mutationObs: MutationObserver | null = null;
  private disabledObs: MutationObserver | null = null;
  private nativeChangeUnlisten: (() => void) | null = null;
  private triggerClickUnlisten: (() => void) | null = null;
  private valueChangesSub: Subscription | null = null;
  private statusChangesSub: Subscription | null = null;

  /** Pending refresh of the open panel; debounced via microtask. */
  private pendingPanelRefresh = false;

  constructor(
    private el: ElementRef<HTMLSelectElement>,
    private renderer: Renderer2,
    private overlay: Overlay,
    private viewContainer: ViewContainerRef,
    @Optional() @Self() private ngControl: NgControl | null
  ) {}

  ngAfterViewInit(): void {
    if (this.el.nativeElement.multiple) {
      // Out of scope for v1 - silently bail out so the page does not break.
      return;
    }
    this.buildTrigger();
    this.attachListeners();
    this.updateTriggerState();
  }

  ngOnDestroy(): void {
    this.closePanel();
    this.mutationObs?.disconnect();
    this.disabledObs?.disconnect();
    this.nativeChangeUnlisten?.();
    this.triggerClickUnlisten?.();
    this.valueChangesSub?.unsubscribe();
    this.statusChangesSub?.unsubscribe();
    if (this.triggerEl && this.triggerEl.parentNode) {
      this.renderer.removeChild(this.triggerEl.parentNode, this.triggerEl);
    }
    this.triggerEl = null;
    this.labelEl = null;
    this.caretEl = null;
  }

  private buildTrigger(): void {
    const select = this.el.nativeElement;
    this.renderer.setStyle(select, 'display', 'none');

    const trigger = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(trigger, 'gtw-searchable-select-trigger');
    // Mirror the styling of the original <select> so layout is preserved.
    const classNames = (select.getAttribute('class') || '')
      .split(/\s+/)
      .filter(Boolean);
    for (const cls of classNames) {
      this.renderer.addClass(trigger, cls);
    }
    this.renderer.setAttribute(trigger, 'role', 'combobox');
    this.renderer.setAttribute(trigger, 'tabindex', '0');
    this.renderer.setAttribute(trigger, 'aria-haspopup', 'listbox');
    this.renderer.setAttribute(trigger, 'aria-expanded', 'false');
    this.renderer.setStyle(trigger, 'cursor', 'pointer');
    this.renderer.setStyle(trigger, 'display', 'flex');
    this.renderer.setStyle(trigger, 'align-items', 'center');
    this.renderer.setStyle(trigger, 'justify-content', 'space-between');
    this.renderer.setStyle(trigger, 'background-color', '#fff');
    this.renderer.setStyle(trigger, 'user-select', 'none');
    this.renderer.setStyle(trigger, 'box-sizing', 'border-box');

    const label = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(label, 'gtw-searchable-select-label');
    this.renderer.setStyle(label, 'overflow', 'hidden');
    this.renderer.setStyle(label, 'text-overflow', 'ellipsis');
    this.renderer.setStyle(label, 'white-space', 'nowrap');
    this.renderer.setStyle(label, 'flex', '1 1 auto');
    this.renderer.setStyle(label, 'min-width', '0');
    this.renderer.appendChild(trigger, label);

    // Use Font Awesome caret to match the rest of the app (FA is bundled
    // globally via styles.scss) and gives a larger, sharper glyph than a
    // raw unicode arrow.
    const caret = this.renderer.createElement('i') as HTMLElement;
    this.renderer.addClass(caret, 'gtw-searchable-select-caret');
    this.renderer.addClass(caret, 'fa');
    this.renderer.addClass(caret, 'fa-caret-down');
    this.renderer.setStyle(caret, 'margin-left', '8px');
    this.renderer.setStyle(caret, 'flex-shrink', '0');
    this.renderer.setStyle(caret, 'color', '#555');
    this.renderer.setStyle(caret, 'font-size', '14px');
    this.renderer.setStyle(caret, 'line-height', '1');
    this.renderer.setAttribute(caret, 'aria-hidden', 'true');
    this.renderer.appendChild(trigger, caret);

    const parent = select.parentNode;
    if (parent) {
      this.renderer.insertBefore(parent, trigger, select);
    }

    this.triggerEl = trigger;
    this.labelEl = label;
    this.caretEl = caret;

    this.triggerClickUnlisten = this.renderer.listen(trigger, 'click', (e: Event) => {
      e.preventDefault();
      e.stopPropagation();
      if (this.isDisabled()) return;
      this.togglePanel();
    });

    this.renderer.listen(trigger, 'keydown', (e: KeyboardEvent) => {
      if (this.isDisabled()) return;
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        this.openPanel();
      }
    });
  }

  private attachListeners(): void {
    const select = this.el.nativeElement;

    // Native 'change' covers programmatic select.value writes that bubble.
    this.nativeChangeUnlisten = this.renderer.listen(select, 'change', () => {
      this.updateTriggerState();
      this.refreshOpenPanel();
    });

    // Track Angular form writes - covers writeValue from ngModel/FormControl
    // which does NOT dispatch a native 'change' event.
    if (this.ngControl?.valueChanges) {
      this.valueChangesSub = this.ngControl.valueChanges.subscribe(() => {
        this.updateTriggerState();
        this.refreshOpenPanel();
      });
    }
    if (this.ngControl?.statusChanges) {
      this.statusChangesSub = this.ngControl.statusChanges.subscribe(() => {
        this.updateTriggerState();
      });
    }

    // Re-build panel option list when *ngFor renders new <option> nodes or
    // option text content changes.
    this.mutationObs = new MutationObserver(() => {
      this.updateTriggerState();
      this.refreshOpenPanel();
    });
    this.mutationObs.observe(select, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    // [disabled]="..." flips the disabled attribute - mirror to the trigger.
    this.disabledObs = new MutationObserver(() => this.updateTriggerState());
    this.disabledObs.observe(select, {
      attributes: true,
      attributeFilter: ['disabled'],
    });
  }

  private isDisabled(): boolean {
    return this.el.nativeElement.disabled;
  }

  private updateTriggerState(): void {
    if (!this.triggerEl || !this.labelEl) return;
    const select = this.el.nativeElement;
    const disabled = this.isDisabled();

    if (disabled) {
      this.renderer.setStyle(this.triggerEl, 'background-color', '#eee');
      this.renderer.setStyle(this.triggerEl, 'cursor', 'not-allowed');
      this.renderer.setStyle(this.triggerEl, 'color', '#999');
      this.renderer.setAttribute(this.triggerEl, 'aria-disabled', 'true');
      this.renderer.setAttribute(this.triggerEl, 'tabindex', '-1');
    } else {
      this.renderer.setStyle(this.triggerEl, 'background-color', '#fff');
      this.renderer.setStyle(this.triggerEl, 'cursor', 'pointer');
      this.renderer.removeStyle(this.triggerEl, 'color');
      this.renderer.removeAttribute(this.triggerEl, 'aria-disabled');
      this.renderer.setAttribute(this.triggerEl, 'tabindex', '0');
    }

    const selected = select.options[select.selectedIndex];
    const selectedIsPlaceholder = selected
      ? this.isPlaceholderValue(selected.value)
      : true;
    const selectedText = (selected?.textContent || '').trim();

    if (selected && !selectedIsPlaceholder && selectedText) {
      this.renderer.setProperty(this.labelEl, 'textContent', selectedText);
      this.renderer.removeStyle(this.labelEl, 'color');
    } else {
      // Prefer the existing placeholder option's label (e.g. "Select...",
      // "Select Account...") so users see exactly the same wording they had
      // in the native <select>. Fall back to the configurable input.
      const placeholderText =
        this.findPlaceholderLabel() || selectedText || this.triggerPlaceholder;
      this.renderer.setProperty(this.labelEl, 'textContent', placeholderText);
      this.renderer.setStyle(this.labelEl, 'color', '#999');
    }
  }

  private getOptions(): GtwSearchableSelectOption[] {
    const select = this.el.nativeElement;
    return Array.from(select.options).map((opt, idx) => ({
      value: opt.value,
      label: (opt.textContent || '').trim(),
      disabled: opt.disabled,
      index: idx,
      isPlaceholder: this.isPlaceholderValue(opt.value),
    }));
  }

  /**
   * Recognises the "no selection" entries: native empty string AND the
   * synthetic keys Angular generates for [ngValue]="null" /
   * [ngValue]="undefined" (e.g. "0: null", "12: undefined").
   */
  private isPlaceholderValue(value: string): boolean {
    if (value === '') return true;
    return /^\d+:\s*(null|undefined)$/.test(value);
  }

  private findPlaceholderLabel(): string {
    const select = this.el.nativeElement;
    for (let i = 0; i < select.options.length; i++) {
      const opt = select.options[i];
      if (this.isPlaceholderValue(opt.value)) {
        return (opt.textContent || '').trim();
      }
    }
    return '';
  }

  private togglePanel(): void {
    if (this.overlayRef) {
      this.closePanel();
    } else {
      this.openPanel();
    }
  }

  private openPanel(): void {
    if (this.overlayRef || !this.triggerEl) return;
    const triggerEl = this.triggerEl;
    const select = this.el.nativeElement;

    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(triggerEl)
      .withFlexibleDimensions(false)
      .withPush(false)
      .withPositions([
        { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: 2 },
        { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -2 },
      ]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
      panelClass: 'gtw-searchable-select-overlay',
      width: triggerEl.offsetWidth,
      maxHeight: 320,
      disposeOnNavigation: true,
    });

    const portal = new ComponentPortal(
      GtwSearchableSelectPanelComponent,
      this.viewContainer
    );
    this.panelRef = this.overlayRef.attach(portal);

    const inst = this.panelRef.instance;
    // Hide placeholder entries ("Select...", "Select Account...") from the
    // panel - they are not real choices; the trigger surfaces them as the
    // greyed-out label when no value is selected.
    inst.options = this.getOptions().filter((o) => !o.isPlaceholder);
    inst.currentValue = select.value;
    inst.width = triggerEl.offsetWidth;
    inst.placeholder = this.searchPlaceholder;
    inst.emptyMessage = this.emptyMessage;
    inst.refresh();

    this.renderer.setAttribute(triggerEl, 'aria-expanded', 'true');

    this.panelSubs = new Subscription();
    this.panelSubs.add(
      inst.select$.subscribe((opt) => {
        this.writeValue(opt.value);
        this.closePanel();
      })
    );
    this.panelSubs.add(inst.close$.subscribe(() => this.closePanel()));
    this.panelSubs.add(
      this.overlayRef.backdropClick().subscribe(() => this.closePanel())
    );
    this.panelSubs.add(
      this.overlayRef
        .keydownEvents()
        .subscribe((event) => {
          if (event.key === 'Escape') {
            event.preventDefault();
            this.closePanel();
          }
        })
    );
  }

  /** Keep in sync with the CSS leave animation duration in the panel. */
  private static readonly LEAVE_ANIMATION_MS = 140;

  private closePanel(): void {
    this.panelSubs?.unsubscribe();
    this.panelSubs = null;

    const overlayRef = this.overlayRef;
    const panel = this.panelRef;
    this.overlayRef = null;
    this.panelRef = null;

    if (this.triggerEl) {
      this.renderer.setAttribute(this.triggerEl, 'aria-expanded', 'false');
      this.triggerEl.focus();
    }

    if (!overlayRef) return;
    if (panel) {
      // Triggers the CSS fade-out via host class binding; dispose after the
      // animation finishes so the user sees the panel slide away instead of
      // it disappearing instantly.
      panel.instance.beginLeave();
      setTimeout(() => overlayRef.dispose(), GtwSearchableSelectDirective.LEAVE_ANIMATION_MS);
    } else {
      overlayRef.dispose();
    }
  }

  /**
   * Programmatically sets the native <select> value and dispatches a bubbling
   * 'change' event so that Angular's SelectControlValueAccessor picks it up
   * exactly as it would for a user click. For [ngValue] object options the
   * value is Angular's synthetic key (e.g. "1: Object") which the accessor's
   * internal _optionMap converts back to the original object.
   */
  private writeValue(value: string): void {
    const select = this.el.nativeElement;
    select.value = value;
    // Native <select> dispatches only 'change' on user interaction, so we
    // mirror exactly that - 'input' is not fired natively for selects and
    // emitting it would surprise consumers using (input).
    select.dispatchEvent(new Event('change', { bubbles: true }));
    this.updateTriggerState();
  }

  private refreshOpenPanel(): void {
    if (!this.panelRef || this.pendingPanelRefresh) return;
    this.pendingPanelRefresh = true;
    // Defer to next microtask so multiple mutations coalesce into one refresh.
    Promise.resolve().then(() => {
      this.pendingPanelRefresh = false;
      if (!this.panelRef) return;
      const inst = this.panelRef.instance;
      inst.options = this.getOptions().filter((o) => !o.isPlaceholder);
      inst.currentValue = this.el.nativeElement.value;
      inst.refresh();
    });
  }
}
